import { UiBaseProvider } from '@faceup/ui-base'
import { type ReactNode, useEffect } from 'react'
import { SavedReportsProvider } from '../Contexts/SavedReportsProvider'
import { useYupLocalization } from '../hooks/useYupLocalization'
import { TrpcProvider } from '../TrpcProvider'
import { useTrackPlatform } from '../utils/analytics'
import { OTAUpdate } from '../utils/OTAUpdate'
import { AccessKeyProvider } from './AccessKeyProvider'
import { AppLockProvider } from './AppLockProvider'
import { AppRouter } from './AppRouter/AppRouter'
import { ClientProvider } from './ClientProvider'
import { CustomApolloProvider } from './CustomApolloProvider'
import { LanguageWrapper } from './LanguageWrapper'
import { useUrlLanguage } from './useUrlLanguage'

/**
 * Router-wide concerns that must run on every route, not just the report/survey
 * branches (whose UiProvider used to host them): the PostHog platform person
 * property and the global yup error localization. Sits inside the base
 * LanguageWrapper so useYupLocalization has an IntlProvider.
 */
const AppGlobals = ({ children }: { children: ReactNode }) => {
  useTrackPlatform()
  useYupLocalization()
  return <>{children}</>
}

/**
 * Providers are top-down and session-stable: client kind, access key and saved reports, then a
 * raw UiBaseProvider (only so the Apollo errorLink can raise notifications), then
 * the single Apollo client, then the router. The language and
 * report-source/survey layers live per route branch.
 *
 * The access key and the saved reports sit above AppLockProvider because its logout() clears
 * both.
 */
export const App = () => {
  useEffect(() => {
    OTAUpdate()
  }, [])

  // Reactive base language: follows the URL :language segment across navigations
  // so it never diverges from the per-branch language after a redirect.
  const baseLanguage = useUrlLanguage()

  return (
    <ClientProvider>
      <AccessKeyProvider>
        <SavedReportsProvider>
          <AppLockProvider>
            <UiBaseProvider direction='ltr' locale={undefined}>
              <CustomApolloProvider>
                {/* Base language for router-level UI (lock screen, NavigationChange,
                    loaders) that renders above the per-branch LanguageWrapper. The
                    route wrappers swap in the report/survey language in place. */}
                <LanguageWrapper language={baseLanguage}>
                  <TrpcProvider>
                    <AppGlobals>
                      <AppRouter />
                    </AppGlobals>
                  </TrpcProvider>
                </LanguageWrapper>
              </CustomApolloProvider>
            </UiBaseProvider>
          </AppLockProvider>
        </SavedReportsProvider>
      </AccessKeyProvider>
    </ClientProvider>
  )
}
